import React, { useState } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Download, Loader2, Eye, Calendar, CreditCard, Clock, User, Mail, FileText, Send } from 'lucide-react';

export const GiftCardsTab = ({ giftCards, clients, loadData }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: 50, client_id: '', recipient_name: '', recipient_email: '', message: '' });
  const [gcClientMode, setGcClientMode] = useState('manual');
  const [gcClientSearch, setGcClientSearch] = useState('');
  const [selectedGc, setSelectedGc] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTarget, setEmailTarget] = useState('');

  const openForm = () => {
    setForm({ amount: 50, client_id: '', recipient_name: '', recipient_email: '', message: '' });
    setGcClientMode('manual');
    setGcClientSearch('');
    setShowForm(true);
  };

  const selectClient = (c) => {
    setForm(p => ({ ...p, client_id: c.id, recipient_name: c.name, recipient_email: c.email }));
    setGcClientSearch(c.name);
  };

  const create = async () => {
    if (!form.amount || form.amount < 1) { toast.error('Montant invalide'); return; }
    try {
      const payload = { ...form };
      if (gcClientMode === 'manual') { payload.client_id = null; }
      const res = await adminAPI.createGiftCard(payload);
      toast.success(`Carte cadeau: ${res.data.code}`);
      setShowForm(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const openDetail = async (gc) => {
    setDetailLoading(true);
    setEditMode(false);
    setEmailTarget('');
    try {
      const res = await adminAPI.getGiftCardDetail(gc.id);
      setSelectedGc(res.data);
    } catch { setSelectedGc(gc); }
    finally { setDetailLoading(false); }
  };

  const startEdit = () => {
    setEditForm({
      balance: selectedGc.balance,
      active: selectedGc.active,
      recipient_name: selectedGc.recipient_name || '',
      recipient_email: selectedGc.recipient_email || '',
      message: selectedGc.message || '',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await adminAPI.editGiftCard(selectedGc.id, editForm);
      toast.success('Carte cadeau modifiée');
      setEditMode(false);
      const res = await adminAPI.getGiftCardDetail(selectedGc.id);
      setSelectedGc(res.data);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const sendEmail = async () => {
    if (!emailTarget.trim()) { toast.error('Email requis'); return; }
    setSendingEmail(true);
    try {
      const res = await adminAPI.sendGiftCardEmail(selectedGc.id, { email: emailTarget.trim() });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur envoi email'); }
    finally { setSendingEmail(false); }
  };

  const usagePercent = (gc) => {
    if (!gc.initial_amount || gc.initial_amount === 0) return 0;
    return Math.round(((gc.initial_amount - gc.balance) / gc.initial_amount) * 100);
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-heading text-lg font-bold text-white">Cartes Cadeaux</h3>
        <Button onClick={openForm} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="create-giftcard-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle carte
        </Button>
      </div>

      <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#111] border-b border-[#2A2A2A]">
              <tr>
                {['Code', 'Montant', 'Solde', 'Destinataire', 'Acheteur', 'Statut', 'Utilisé', 'Date'].map(h => (
                  <th key={h} className="text-left p-3 font-semibold text-slate-400 text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {giftCards.map(gc => (
                <tr key={gc.id} className="border-b border-[#1A1A1A] hover:bg-[#222] cursor-pointer transition-colors" onClick={() => openDetail(gc)} data-testid={`gc-row-${gc.id}`}>
                  <td className="p-3 font-mono text-xs text-slate-300">{gc.code}</td>
                  <td className="p-3 font-semibold text-white">{gc.initial_amount}€</td>
                  <td className="p-3">
                    <span className={`font-semibold ${gc.balance > 0 ? 'text-[#00FF00]' : 'text-slate-500'}`}>{gc.balance}€</span>
                  </td>
                  <td className="p-3 text-slate-400 text-xs">{gc.recipient_name || gc.recipient_email || '—'}</td>
                  <td className="p-3 text-slate-500 text-xs">{gc.buyer_name || '—'}</td>
                  <td className="p-3">
                    <Badge className={gc.active ? 'bg-[#00FF00]/15 text-[#00FF00] border-0 text-[10px]' : 'bg-[#333] text-slate-500 border-0 text-[10px]'}>
                      {gc.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="w-16 h-1.5 bg-[#2A2A2A] rounded-full overflow-hidden">
                      <div className="h-full bg-[#00FF00] rounded-full" style={{ width: `${usagePercent(gc)}%` }} />
                    </div>
                    <span className="text-[9px] text-slate-500">{usagePercent(gc)}%</span>
                  </td>
                  <td className="p-3 text-slate-500 text-xs">{gc.created_at?.split('T')[0]}</td>
                </tr>
              ))}
              {giftCards.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-600">Aucune carte cadeau</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md bg-[#1A1A1A] border-[#2A2A2A] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Nouvelle carte cadeau</DialogTitle>
            <DialogDescription className="text-slate-500">Active immédiatement</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-300">Montant (€)</Label>
              <Input type="number" min={1} value={form.amount} onChange={e => setForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="giftcard-amount" />
            </div>
            <div className="flex gap-2">
              <Button variant={gcClientMode === 'existing' ? 'default' : 'outline'} size="sm" onClick={() => setGcClientMode('existing')} className={gcClientMode === 'existing' ? 'bg-[#00FF00] text-black' : 'text-slate-400 border-[#2A2A2A]'} data-testid="gc-client-mode-existing">Client existant</Button>
              <Button variant={gcClientMode === 'manual' ? 'default' : 'outline'} size="sm" onClick={() => { setGcClientMode('manual'); setForm(p => ({ ...p, client_id: '' })); }} className={gcClientMode === 'manual' ? 'bg-[#00FF00] text-black' : 'text-slate-400 border-[#2A2A2A]'} data-testid="gc-client-mode-manual">Saisie manuelle</Button>
            </div>
            {gcClientMode === 'existing' ? (
              <div>
                <Label className="text-sm font-semibold text-slate-300">Rechercher un client</Label>
                <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" /><Input value={gcClientSearch} onChange={e => { setGcClientSearch(e.target.value); setForm(p => ({ ...p, client_id: '' })); }} placeholder="Nom ou email..." className="bg-[#0A0A0A] border-[#2A2A2A] text-white pl-10" data-testid="gc-client-search" /></div>
                {gcClientSearch && !form.client_id && (
                  <div className="max-h-32 overflow-y-auto bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg mt-1">
                    {clients.filter(c => c.name?.toLowerCase().includes(gcClientSearch.toLowerCase()) || c.email?.toLowerCase().includes(gcClientSearch.toLowerCase())).slice(0, 8).map(c => (
                      <button key={c.id} onClick={() => selectClient(c)} className="w-full text-left p-2 hover:bg-[#222] flex justify-between items-center" data-testid={`gc-select-client-${c.id}`}>
                        <span className="text-white text-sm">{c.name}</span><span className="text-xs text-slate-500">{c.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                {form.client_id && <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-0 mt-2">{form.recipient_name} - {form.recipient_email}</Badge>}
              </div>
            ) : (<>
              <div><Label className="text-sm text-slate-300">Destinataire</Label><Input value={form.recipient_name} onChange={e => setForm(p => ({ ...p, recipient_name: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" /></div>
              <div><Label className="text-sm text-slate-300">Email</Label><Input value={form.recipient_email} onChange={e => setForm(p => ({ ...p, recipient_email: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" /></div>
            </>)}
            <div><Label className="text-sm text-slate-300">Message</Label><Textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={2} className="bg-[#0A0A0A] border-[#2A2A2A] text-white resize-none" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-slate-400">Annuler</Button>
            <Button onClick={create} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="save-giftcard-btn">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / Edit Dialog */}
      <Dialog open={!!selectedGc} onOpenChange={v => { if (!v) { setSelectedGc(null); setEditMode(false); } }}>
        <DialogContent className="sm:max-w-lg bg-[#1A1A1A] border-[#2A2A2A] max-h-[90vh] overflow-y-auto" data-testid="gc-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">
              {editMode ? 'Modifier la carte' : 'Détail carte cadeau'}
            </DialogTitle>
            <DialogDescription className="sr-only">Détails de la carte cadeau</DialogDescription>
          </DialogHeader>

          {detailLoading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#00FF00]" /></div>}

          {selectedGc && !detailLoading && !editMode && (
            <div className="space-y-4">
              {/* Card preview */}
              <div className="w-full aspect-[1.7/1] rounded-xl bg-[#00FF00] flex flex-col justify-between p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#00CC00] rounded-bl-full" />
                <div className="flex justify-between items-start relative z-10">
                  <span className="font-heading text-sm font-bold text-black">RIDINGWATT</span>
                  <span className="font-heading text-xs font-bold text-black/50 uppercase">Carte Cadeau</span>
                </div>
                {selectedGc.recipient_name && <p className="text-sm text-black/70">Pour : {selectedGc.recipient_name}</p>}
                {selectedGc.message && <p className="text-xs text-black/60 italic">"{selectedGc.message}"</p>}
                <div className="relative z-10">
                  <p className="text-[10px] text-black/50 mb-0.5">Valeur</p>
                  <p className="font-heading text-3xl font-bold text-black">{selectedGc.initial_amount}€</p>
                </div>
                <p className="font-mono text-xs text-black/70 tracking-wider relative z-10" data-testid="gc-detail-code">{selectedGc.code}</p>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center border border-[#2A2A2A]">
                  <p className="text-xs text-slate-500">Montant initial</p>
                  <p className="text-lg font-bold text-white">{selectedGc.initial_amount}€</p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center border border-[#2A2A2A]">
                  <p className="text-xs text-slate-500">Solde restant</p>
                  <p className={`text-lg font-bold ${selectedGc.balance > 0 ? 'text-[#00FF00]' : 'text-red-400'}`}>{selectedGc.balance}€</p>
                </div>
                <div className="bg-[#0A0A0A] rounded-lg p-3 text-center border border-[#2A2A2A]">
                  <p className="text-xs text-slate-500">Statut</p>
                  <Badge className={`mt-1 ${selectedGc.active ? 'bg-[#00FF00]/15 text-[#00FF00]' : 'bg-red-500/15 text-red-400'} border-0`}>
                    {selectedGc.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>

              {/* Details */}
              <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#2A2A2A] space-y-2 text-xs">
                {selectedGc.recipient_email && (
                  <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-slate-500" /><span className="text-slate-400">Destinataire : {selectedGc.recipient_email}</span></div>
                )}
                {selectedGc.buyer_name && (
                  <div className="flex items-center gap-2"><User className="w-3 h-3 text-slate-500" /><span className="text-slate-400">Acheteur : {selectedGc.buyer_name} ({selectedGc.buyer_email})</span></div>
                )}
                <div className="flex items-center gap-2"><Calendar className="w-3 h-3 text-slate-500" /><span className="text-slate-400">Créée le {selectedGc.created_at?.split('T')[0]}</span></div>
                {selectedGc.activated_at && (
                  <div className="flex items-center gap-2"><Clock className="w-3 h-3 text-slate-500" /><span className="text-slate-400">Activée le {selectedGc.activated_at?.split('T')[0]}</span></div>
                )}
                {selectedGc.payment_provider && (
                  <div className="flex items-center gap-2"><CreditCard className="w-3 h-3 text-slate-500" /><span className="text-slate-400">Paiement : {selectedGc.payment_provider === 'revolut' ? 'Revolut' : selectedGc.payment_provider === 'admin' ? 'Admin (gratuit)' : selectedGc.payment_provider}</span></div>
                )}
              </div>

              {/* Usage History */}
              {(selectedGc.usage_history?.length > 0 || selectedGc.bookings_used?.length > 0) && (
                <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#2A2A2A]">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Historique d'utilisation</p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {(selectedGc.usage_history || []).map((u, i) => (
                      <div key={u.booking_id || `${u.date}-${i}`} className="flex items-center justify-between bg-[#1A1A1A] rounded px-2 py-1.5" data-testid={`gc-usage-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-red-400">-{u.amount}€</span>
                          <span className="text-[10px] text-slate-400">{u.activity_name || 'Réservation'}</span>
                        </div>
                        <span className="text-[9px] text-slate-600">{u.date ? new Date(u.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : ''}</span>
                      </div>
                    ))}
                    {(selectedGc.bookings_used || []).filter(b => !(selectedGc.usage_history || []).find(u => u.booking_id === b.id)).map(b => (
                      <div key={b.id} className="flex items-center justify-between bg-[#1A1A1A] rounded px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-red-400">-{b.price}€</span>
                          <span className="text-[10px] text-slate-400">{b.activity_name} ({b.date})</span>
                        </div>
                        <Badge className={`border-0 text-[8px] ${b.status === 'confirmed' ? 'bg-[#00FF00]/15 text-[#00FF00]' : 'bg-yellow-500/15 text-yellow-400'}`}>{b.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button onClick={startEdit} className="flex-1 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-xs" data-testid="gc-edit-btn">
                  <Pencil className="w-3 h-3 mr-1" /> Modifier
                </Button>
                <a href={adminAPI.getGiftCardPdfUrl(selectedGc.id)} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-[#2A2A2A] text-white text-xs font-medium hover:bg-[#333] transition-colors" data-testid="gc-download-pdf">
                  <Download className="w-3 h-3" /> PDF
                </a>
              </div>

              {/* Send email */}
              <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#2A2A2A]">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Envoyer par email</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={emailTarget}
                    onChange={e => setEmailTarget(e.target.value)}
                    placeholder={selectedGc.recipient_email || 'Email...'}
                    className="flex-1 bg-[#1A1A1A] border-[#2A2A2A] text-white text-xs h-8"
                    data-testid="gc-email-target"
                  />
                  <Button onClick={sendEmail} disabled={sendingEmail} className="bg-amber-500 hover:bg-amber-600 text-black font-bold text-xs h-8 px-3" data-testid="gc-send-email-btn">
                    {sendingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                    Envoyer
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Mode */}
          {selectedGc && !detailLoading && editMode && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-300">Solde (€)</Label>
                <Input type="number" min={0} step="0.01" value={editForm.balance} onChange={e => setEditForm(p => ({ ...p, balance: parseFloat(e.target.value) || 0 }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="gc-edit-balance" />
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-sm text-slate-300">Active</Label>
                <button onClick={() => setEditForm(p => ({ ...p, active: !p.active }))}
                  className={`w-12 h-6 rounded-full transition-colors ${editForm.active ? 'bg-[#00FF00]' : 'bg-[#333]'}`} data-testid="gc-edit-active-toggle">
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${editForm.active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div>
                <Label className="text-sm text-slate-300">Destinataire</Label>
                <Input value={editForm.recipient_name} onChange={e => setEditForm(p => ({ ...p, recipient_name: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="gc-edit-recipient-name" />
              </div>
              <div>
                <Label className="text-sm text-slate-300">Email destinataire</Label>
                <Input value={editForm.recipient_email} onChange={e => setEditForm(p => ({ ...p, recipient_email: e.target.value }))} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="gc-edit-recipient-email" />
              </div>
              <div>
                <Label className="text-sm text-slate-300">Message</Label>
                <Textarea value={editForm.message} onChange={e => setEditForm(p => ({ ...p, message: e.target.value }))} rows={2} className="bg-[#0A0A0A] border-[#2A2A2A] text-white resize-none" data-testid="gc-edit-message" />
              </div>
              <div className="flex gap-2">
                <Button onClick={saveEdit} disabled={saving} className="flex-1 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-xs" data-testid="gc-save-edit">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Enregistrer
                </Button>
                <Button onClick={() => setEditMode(false)} variant="outline" className="border-[#2A2A2A] text-slate-400 bg-transparent text-xs">Annuler</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
